import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  MinusOutlined,
  PlusOutlined as ExpandPlusOutlined,
  DragOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../api/api";

const { Title, Text } = Typography;
const { Search } = Input;

const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const childTableHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "56px minmax(160px, 1.6fr) 100px 110px 150px 150px minmax(180px, 1.4fr) 140px",
  background: "#f5f5f5",
  borderBottom: "1px solid #e8e8e8",
  fontSize: 13,
  fontWeight: 600,
  color: "#595959",
};

const childTableRowStyle = {
  display: "grid",
  gridTemplateColumns: "56px minmax(160px, 1.6fr) 100px 110px 150px 150px minmax(180px, 1.4fr) 140px",
  borderBottom: "1px solid #f0f0f0",
  background: "#fff",
};

const childCellStyle = {
  padding: "10px 12px",
  borderRight: "1px solid #f0f0f0",
  display: "flex",
  alignItems: "center",
  minHeight: 48,
};

const childLastCellStyle = {
  ...childCellStyle,
  borderRight: "none",
};

const compactDateTextStyle = {
  fontSize: 13,
  lineHeight: 1.4,
};

const pageUrlStyle = {
  display: "block",
  fontSize: 13,
  lineHeight: 1.4,
  overflowWrap: "anywhere",
};

const DestinationManagementPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [draggingParentId, setDraggingParentId] = useState(null);
  const [draggingChildId, setDraggingChildId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [pendingCountryOrder, setPendingCountryOrder] = useState(null);
  const [pendingChildOrders, setPendingChildOrders] = useState({});

  const destinationsById = useMemo(
    () =>
      destinations.reduce((acc, item) => {
        acc[Number(item.id)] = item;
        return acc;
      }, {}),
    [destinations]
  );

  const fetchDestinations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}destinations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await res.json();

      if (res.ok && result.success) {
        const rows = Array.isArray(result.data) ? result.data : [];
        setDestinations(rows);
        setExpandedRowKeys(
          rows
            .filter((item) => item.type === "country")
            .map((item) => `country-${item.id}`)
        );
        setPendingCountryOrder(null);
        setPendingChildOrders({});
      } else {
        message.error(result.error || "Failed to load destinations");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load destinations");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDestinations();
  }, [fetchDestinations]);

  const handleDelete = async (record) => {
    try {
      const res = await fetch(`${API_BASE_URL}destinations`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: record.id }),
      });
      const result = await res.json();

      if (res.ok && result.success) {
        message.success("Destination deleted");
        fetchDestinations();
      } else {
        message.error(result.error || "Failed to delete destination");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to delete destination");
    }
  };

  const keyword = searchText.trim().toLowerCase();

  const normalizeSlug = (value) =>
    String(value || "")
      .trim()
      .replace(/^\/+|\/+$/g, "");

  const buildDestinationPath = useCallback(
    (record) => {
      const slug = normalizeSlug(record.display_slug || record.slug);

      if (!slug) {
        return "";
      }

      if (record.type === "city" && record.parent_id) {
        const parent = destinationsById[Number(record.parent_id)];
        const parentSlug = normalizeSlug(parent?.display_slug || parent?.slug);

        if (parentSlug) {
          if (slug === parentSlug || slug.startsWith(`${parentSlug}/`)) {
            return `/${slug}`;
          }

          return `/${parentSlug}/${slug}`;
        }
      }

      return `/${slug}`;
    },
    [destinationsById]
  );

  const persistOrder = async (payload) => {
    const res = await fetch(`${API_BASE_URL}destinationReorder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const result = await res.json();

    if (!res.ok || !result.success) {
      throw new Error(result.error || "Failed to update destination order");
    }
  };

  const matchesFilters = useCallback(
    (item) => {
      const pagePath = buildDestinationPath(item).toLowerCase();
      const matchesSearch =
        !keyword ||
        String(item.id).includes(keyword) ||
        (item.display_name || "").toLowerCase().includes(keyword) ||
        (item.display_slug || item.slug || "").toLowerCase().includes(keyword) ||
        pagePath.includes(keyword);

      const matchesStatus = !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    },
    [buildDestinationPath, keyword, statusFilter]
  );

  const tableRows = useMemo(() => {
    const childrenByParentId = destinations.reduce((acc, item) => {
      if (!item.parent_id) {
        return acc;
      }

      const parentId = Number(item.parent_id);
      if (!acc[parentId]) {
        acc[parentId] = [];
      }

      acc[parentId].push(item);
      return acc;
    }, {});

    const groupedRows = destinations
      .filter((item) => item.type === "country")
      .slice()
      .sort((a, b) => {
        const sortA = Number(a.sort_order || 0);
        const sortB = Number(b.sort_order || 0);

        if (sortA !== sortB) {
          return sortA - sortB;
        }

        return Number(a.id) - Number(b.id);
      })
      .map((country) => {
        const allChildren = (childrenByParentId[Number(country.id)] || [])
          .slice()
          .sort((a, b) => {
            const sortA = Number(a.child_sort_order || 0);
            const sortB = Number(b.child_sort_order || 0);

            if (sortA !== sortB) {
              return sortA - sortB;
            }

            return Number(a.id) - Number(b.id);
          });

        const visibleChildren = allChildren.filter((child) => matchesFilters(child));
        const parentMatches = matchesFilters(country);

        if (!parentMatches && visibleChildren.length === 0) {
          return null;
        }

        return {
          ...country,
          key: `country-${country.id}`,
          rowType: "country",
          visibleChildren,
          allChildren,
        };
      })
      .filter(Boolean);

    const groupedIds = new Set(
      groupedRows.flatMap((row) => [
        Number(row.id),
        ...row.allChildren.map((child) => Number(child.id)),
      ])
    );

    const standaloneRows = destinations
      .filter((item) => {
        if (!matchesFilters(item)) {
          return false;
        }

        if (groupedIds.has(Number(item.id))) {
          return false;
        }

        if (item.parent_id) {
          const parent = destinationsById[Number(item.parent_id)];
          return !parent || parent.type !== "country";
        }

        return true;
      })
      .slice()
      .sort((a, b) => {
        const sortA =
          a.type === "city"
            ? Number(a.child_sort_order ?? a.sort_order ?? 0)
            : Number(a.sort_order || 0);
        const sortB =
          b.type === "city"
            ? Number(b.child_sort_order ?? b.sort_order ?? 0)
            : Number(b.sort_order || 0);

        if (sortA !== sortB) {
          return sortA - sortB;
        }

        return Number(a.id) - Number(b.id);
      })
      .map((item) => ({
        ...item,
        key: `standalone-${item.id}`,
        rowType: "standalone",
      }));

    return [...groupedRows, ...standaloneRows];
  }, [destinations, destinationsById, matchesFilters]);

  const renderTypeTag = (value) => (
    <Tag color={value === "country" ? "blue" : "purple"}>{value}</Tag>
  );

  const renderStatusTag = (value) => {
    const color =
      value === "published" ? "green" : value === "archived" ? "default" : "gold";
    return <Tag color={color}>{value}</Tag>;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    return value;
  };

  const formatPageUrl = (record) => {
    const path = buildDestinationPath(record);

    if (record.status !== "published" || !path) {
      return null;
    }

    return `https://sunbook.ca/en${path}`;
  };

  const renderActionButtons = (record) => (
    <Space size={6} wrap>
      <Button
        icon={<EditOutlined />}
        size="small"
        onClick={() => navigate(`/destinations/${record.id}`)}
      >
        Edit
      </Button>
      <Popconfirm
        title="Delete destination?"
        description="This permanently removes the destination and all translations."
        okText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDelete(record)}
      >
        <Button danger icon={<DeleteOutlined />} size="small">
          Delete
        </Button>
      </Popconfirm>
    </Space>
  );

  const hasPendingOrderChanges =
    Boolean(pendingCountryOrder) || Object.keys(pendingChildOrders).length > 0;

  const handleSaveOrder = async () => {
    if (!hasPendingOrderChanges) {
      return;
    }

    setSavingOrder(true);
    try {
      if (pendingCountryOrder) {
        await persistOrder({
          scope: "country",
          ordered_ids: pendingCountryOrder,
        });
      }

      const childEntries = Object.entries(pendingChildOrders);
      for (const [parentId, orderedIds] of childEntries) {
        await persistOrder({
          scope: "child",
          parent_id: Number(parentId),
          ordered_ids: orderedIds,
        });
      }

      message.success("Destination order saved");
      fetchDestinations();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Failed to save destination order");
    } finally {
      setSavingOrder(false);
    }
  };

  const reorderCountryRows = async (draggedRecord, targetRecord) => {
    const countryRows = destinations
      .filter((row) => row.type === "country")
      .slice()
      .sort((a, b) => {
        const sortA = Number(a.sort_order || 0);
        const sortB = Number(b.sort_order || 0);

        if (sortA !== sortB) {
          return sortA - sortB;
        }

        return Number(a.id) - Number(b.id);
      });
    const dragIndex = countryRows.findIndex((row) => row.id === draggedRecord.id);
    const hoverIndex = countryRows.findIndex((row) => row.id === targetRecord.id);

    if (dragIndex === -1 || hoverIndex === -1 || dragIndex === hoverIndex) {
      return;
    }

    const reorderedCountries = [...countryRows];
    const [movedCountry] = reorderedCountries.splice(dragIndex, 1);
    reorderedCountries.splice(hoverIndex, 0, movedCountry);

    setDestinations((current) => {
      const reorderedCountryMap = new Map(
        reorderedCountries.map((country, index) => [Number(country.id), index + 1])
      );

      return current.map((item) =>
        item.type === "country" && reorderedCountryMap.has(Number(item.id))
          ? {
              ...item,
              sort_order: reorderedCountryMap.get(Number(item.id)),
            }
          : item
      );
    });
    setPendingCountryOrder(reorderedCountries.map((country) => country.id));
  };

  const reorderChildRows = async (parentRecord, draggedChildId, targetChildId) => {
    const children = [...parentRecord.allChildren];
    const dragIndex = children.findIndex((child) => child.id === draggedChildId);
    const hoverIndex = children.findIndex((child) => child.id === targetChildId);

    if (dragIndex === -1 || hoverIndex === -1 || dragIndex === hoverIndex) {
      return;
    }

    const [movedChild] = children.splice(dragIndex, 1);
    children.splice(hoverIndex, 0, movedChild);

    setDestinations((current) => {
      const reorderedChildMap = new Map(
        children.map((child, index) => [Number(child.id), index + 1])
      );

      return current.map((item) =>
        Number(item.parent_id) === Number(parentRecord.id) &&
        reorderedChildMap.has(Number(item.id))
          ? {
              ...item,
              child_sort_order: reorderedChildMap.get(Number(item.id)),
            }
          : item
      );
    });
    setPendingChildOrders((current) => ({
      ...current,
      [parentRecord.id]: children.map((child) => child.id),
    }));
  };

  const columns = [
    {
      title: "",
      key: "drag_handle",
      width: 44,
      render: (_, record) =>
        record.rowType === "country" ? (
          <DragOutlined style={{ color: "#8c8c8c", cursor: "grab" }} />
        ) : null,
    },
    {
      title: "Destination",
      dataIndex: "display_name",
      key: "display_name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.display_name || "-"}</Text>
          <Text type="secondary">#{record.id}</Text>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (value) => renderTypeTag(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value) => renderStatusTag(value),
    },
    {
      title: "Published At",
      dataIndex: "published_at",
      key: "published_at",
      width: 150,
      render: (value) => (
        <Text type="secondary" style={compactDateTextStyle}>
          {formatDateTime(value)}
        </Text>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value) => (
        <Text type="secondary" style={compactDateTextStyle}>
          {formatDateTime(value)}
        </Text>
      ),
    },
    {
      title: "Page URL",
      key: "page_url",
      width: 220,
      render: (_, record) => {
        const pageUrl = formatPageUrl(record);
        return pageUrl ? (
          <a href={pageUrl} target="_blank" rel="noreferrer" style={pageUrlStyle}>
            {pageUrl}
          </a>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => renderActionButtons(record),
    },
  ];

  const renderExpandedContent = (record) => {
    if (record.visibleChildren.length === 0) {
      return (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No child destinations"
          />
        </div>
      );
    }

    return (
      <div
        style={{
          margin: "8px 0 8px 48px",
          border: "1px solid #f0f0f0",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div style={childTableHeaderStyle}>
          <div style={childCellStyle}>Drag</div>
          <div style={childCellStyle}>Destination</div>
          <div style={childCellStyle}>Type</div>
          <div style={childCellStyle}>Status</div>
          <div style={childCellStyle}>Published At</div>
          <div style={childCellStyle}>Created At</div>
          <div style={childCellStyle}>Page URL</div>
          <div style={childLastCellStyle}>Actions</div>
        </div>
        {record.visibleChildren.map((child, index) => (
          <div
            key={child.id}
            style={{
              ...childTableRowStyle,
              borderBottom:
                index === record.visibleChildren.length - 1
                  ? "none"
                  : childTableRowStyle.borderBottom,
              background:
                draggingChildId === child.id ? "#fafafa" : childTableRowStyle.background,
            }}
            draggable
            onDragStart={() => setDraggingChildId(child.id)}
            onDragEnd={() => setDraggingChildId(null)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={async () => {
              if (draggingChildId && draggingChildId !== child.id) {
                await reorderChildRows(record, draggingChildId, child.id);
              }
              setDraggingChildId(null);
            }}
          >
            <div style={childCellStyle}>
              <DragOutlined style={{ color: "#8c8c8c", cursor: "grab" }} />
            </div>
            <div style={childCellStyle}>
              <Space direction="vertical" size={0}>
                <Text strong>{child.display_name || "-"}</Text>
                <Text type="secondary">#{child.id}</Text>
              </Space>
            </div>
            <div style={childCellStyle}>{renderTypeTag(child.type)}</div>
            <div style={childCellStyle}>{renderStatusTag(child.status)}</div>
            <div style={childCellStyle}>
              <Text type="secondary" style={compactDateTextStyle}>
                {formatDateTime(child.published_at)}
              </Text>
            </div>
            <div style={childCellStyle}>
              <Text type="secondary" style={compactDateTextStyle}>
                {formatDateTime(child.created_at)}
              </Text>
            </div>
            <div style={childCellStyle}>
              {formatPageUrl(child) ? (
                <a
                  href={formatPageUrl(child)}
                  target="_blank"
                  rel="noreferrer"
                  style={pageUrlStyle}
                >
                  {formatPageUrl(child)}
                </a>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </div>
            <div style={childLastCellStyle}>{renderActionButtons(child)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 4 }}>
            Destination Management
          </Title>
          <Text type="secondary">
            Manage shared destination records and locale-specific content.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              onClick={handleSaveOrder}
              loading={savingOrder}
              disabled={!hasPendingOrderChanges}
            >
              Save Order
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/destinations/new")}
            >
              New Destination
            </Button>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={10}>
            <Search
              allowClear
              placeholder="Search by id, name, or page url"
              onSearch={setSearchText}
              onChange={(event) => setSearchText(event.target.value)}
              value={searchText}
            />
          </Col>
          <Col xs={24} md={14}>
            <Select
              allowClear
              placeholder="Filter by status"
              options={STATUS_OPTIONS}
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Col>
        </Row>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="key"
          size="small"
          loading={loading || savingOrder}
          dataSource={tableRows}
          columns={columns}
          pagination={false}
          locale={{ emptyText: <Empty description="No destinations found" /> }}
          onRow={(record) =>
            record.rowType === "country"
              ? {
                  draggable: true,
                  onDragStart: () => setDraggingParentId(record.id),
                  onDragEnd: () => setDraggingParentId(null),
                  onDragOver: (event) => event.preventDefault(),
                  onDrop: async () => {
                    if (draggingParentId && draggingParentId !== record.id) {
                      const draggedRecord = tableRows.find(
                        (row) => row.rowType === "country" && row.id === draggingParentId
                      );
                      if (draggedRecord) {
                        await reorderCountryRows(draggedRecord, record);
                      }
                    }
                    setDraggingParentId(null);
                  },
                  style: {
                    cursor: "move",
                    backgroundColor:
                      draggingParentId === record.id ? "#fafafa" : undefined,
                  },
                }
              : {}
          }
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) =>
              setExpandedRowKeys(keys.map((key) => String(key))),
            expandedRowRender: renderExpandedContent,
            rowExpandable: (record) =>
              record.rowType === "country" && record.allChildren.length > 0,
            expandIconColumnIndex: 1,
            expandIcon: ({ expanded, onExpand, record }) =>
              record.rowType === "country" && record.allChildren.length > 0 ? (
                <Button
                  type="text"
                  size="small"
                  icon={expanded ? <MinusOutlined /> : <ExpandPlusOutlined />}
                  onClick={(event) => onExpand(record, event)}
                  style={{ color: "#595959" }}
                />
              ) : null,
          }}
        />
      </Card>
    </div>
  );
};

export default DestinationManagementPage;
